import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  Avatar,
  IconButton,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Landscape,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LanguageSwitcher from './LanguageSwitcher';

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(username, password);
    
    if (!result.success) {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000062 0%, #000043 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            padding: 4,
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)'
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
              <LanguageSwitcher variant="menu" />
            </Box>
            <Avatar
              sx={{
                mx: 'auto',
                mb: 2,
                bgcolor: '#000062',
                width: 64,
                height: 64
              }}
            >
              <Landscape sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 'bold',
                color: '#000062',
                mb: 1
              }}
            >
              Paramount Landscaping
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ mb: 2 }}
            >
              {t('auth.loginSubtitle')}
            </Typography>
          </Box>

          <Card elevation={0} sx={{ bgcolor: 'transparent' }}>
            <CardContent>
              <Box component="form" onSubmit={handleSubmit}>
                <Typography
                  variant="h5"
                  component="h2"
                  sx={{
                    textAlign: 'center',
                    mb: 3,
                    fontWeight: 'medium',
                    color: '#000062'
                  }}
                >
                  {t('auth.loginTitle')}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <TextField
                  fullWidth
                  label={t('auth.username')}
                  variant="outlined"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  inputProps={{ 'aria-label': t('auth.username') }}
                  sx={{ mb: 2 }}
                  InputProps={{
                    sx: { borderRadius: 2 }
                  }}
                />

                <TextField
                  fullWidth
                  label={t('auth.password')}
                  type={showPassword ? 'text' : 'password'}
                  variant="outlined"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  inputProps={{ 'aria-label': t('auth.password') }}
                  sx={{ mb: 3 }}
                  InputProps={{
                    sx: { borderRadius: 2 },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}>
                          <IconButton
                            onClick={() => setShowPassword((v) => !v)}
                            onMouseDown={(e) => e.preventDefault()}
                            edge="end"
                            aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    borderRadius: 2,
                    backgroundColor: '#000062',
                    '&:hover': {
                      backgroundColor: '#000043'
                    },
                    fontSize: '1.1rem',
                    fontWeight: 'medium'
                  }}
                >
                  {loading ? t('common.loading') : t('auth.signIn')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage; 